import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (next: number) => void;
  /** Optional context label (e.g. "users") used in the screen-reader label. */
  itemLabel?: string;
}

/**
 * Generic 1-based pagination control. Renders nothing when `pageCount <= 1`.
 *
 * Layout: First, Prev, [windowed page numbers with ellipses for >7 pages],
 * Next, Last. Up to 7 numeric buttons visible; for very long ranges the
 * window centers on the current page with `…` separators.
 */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  itemLabel = 'rows',
}: PaginationProps) {
  if (pageCount <= 1) return null;

  const safePage = Math.min(Math.max(1, page), pageCount);

  const go = (next: number) => {
    const clamped = Math.min(Math.max(1, next), pageCount);
    if (clamped !== safePage) onPageChange(clamped);
  };

  const numbers = visibleNumbers(safePage, pageCount);

  return (
    <nav
      className="flex items-center gap-1 text-sm"
      aria-label={`${itemLabel} pagination`}
    >
      <ArrowButton
        disabled={safePage === 1}
        onClick={() => go(1)}
        aria-label="First page"
      >
        <ChevronsLeft className="w-4 h-4" />
      </ArrowButton>
      <ArrowButton
        disabled={safePage === 1}
        onClick={() => go(safePage - 1)}
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </ArrowButton>

      {numbers.map((entry, idx) =>
        entry === 'ellipsis' ? (
          <span
            key={`gap-${idx}`}
            className="px-2 text-slate-400 select-none"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <PageButton
            key={entry}
            active={entry === safePage}
            onClick={() => go(entry)}
          >
            {entry}
          </PageButton>
        ),
      )}

      <ArrowButton
        disabled={safePage === pageCount}
        onClick={() => go(safePage + 1)}
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </ArrowButton>
      <ArrowButton
        disabled={safePage === pageCount}
        onClick={() => go(pageCount)}
        aria-label="Last page"
      >
        <ChevronsRight className="w-4 h-4" />
      </ArrowButton>
    </nav>
  );
}

function ArrowButton({
  disabled,
  onClick,
  children,
  ...rest
}: {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  'aria-label': string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-2 py-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
      {...rest}
    >
      {children}
    </button>
  );
}

function PageButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={
        active
          ? 'min-w-8 px-2 py-1 rounded font-medium bg-brand-orange text-white'
          : 'min-w-8 px-2 py-1 rounded text-slate-600 hover:bg-slate-100'
      }
    >
      {children}
    </button>
  );
}

/** Build the page-button window. ≤7 pages → all numbers. Otherwise window
 *  with ellipses around the current page so we never render more than ~7
 *  numeric buttons. */
function visibleNumbers(page: number, pageCount: number): (number | 'ellipsis')[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const out: (number | 'ellipsis')[] = [1];
  const left = Math.max(2, page - 1);
  const right = Math.min(pageCount - 1, page + 1);
  if (left > 2) out.push('ellipsis');
  for (let i = left; i <= right; i += 1) out.push(i);
  if (right < pageCount - 1) out.push('ellipsis');
  out.push(pageCount);
  return out;
}
